// @ts-nocheck - Los errores de JSX.IntrinsicElements son falsos positivos del linter
// El código compila y funciona correctamente con React 18
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import InfoProcesos from '../components/infoProcesos'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar, Line, Pie } from 'react-chartjs-2'
import apiService from '../services/api.service'
import { LoadingOverlay } from '../components/loading'
import { 
  FinancialData, 
  HistoricalData, 
  FincaInfo, 
  CalculatorValues, 
  CalculationResults, 
  ChartData, 
  FinancialFormulas, 
  FarmTypeUnits, 
  CategoryOrder, 
  ChartOptions,
  RubroYearSummary
} from '../types'
import './proceso-calculo.css'

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Función helper para dibujar rectángulo redondeado (se usará en el plugin)
const drawRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

// Financial calculation formulas
const FINANCIAL_FORMULAS: FinancialFormulas = {
  costoDia: (totalCostos: number, totalGastos: number, inventario: number, costoGanadoEnPie: number): number => 
    (Math.abs(totalCostos + totalGastos - costoGanadoEnPie) / 30) / inventario,
  produccionNecesaria: (totalCostos: number, totalGastos: number, precio: number): number => 
    (Math.abs(totalCostos + totalGastos) / 30) / precio,
  proyeccionGananciaDiaria: (promedio: number, precio: number): number => promedio * precio,
  gastosOperativos: (totalCostos: number, totalGastos: number, costoGanadoEnPie: number): number => 
    Math.abs(totalCostos + totalGastos - costoGanadoEnPie),
  utilidadDiaria: (proyeccionGananciaDiaria: number, costoDia: number): number => 
    proyeccionGananciaDiaria - costoDia,
  proyeccionGastosAumentados: (gastosOperativos: number, porcentaje: number): number => gastosOperativos * (1 + porcentaje/100)
};

// Define the order of groups and categories
const GROUP_ORDER: string[] = ["Ventas", "Compras", "Insumos", "Nomina", "Administrativo", "Otros"];

/** Orden de visualización en el gráfico de inventario (códigos de categoría de ganado). */
const INVENTARIO_CATEGORIA_CODIGO_ORDER: string[] = [
  'VP',
  'CM',
  'CH',
  'VHP',
  'V. Prod Vacia',
  'V. Prod Preñ',
  'N.V Preñ',
  'V.tre Prog',
  'HL',
  'NVV',
  'VHV',
  'ML',
  'MC',
  'TRE',
  'TRO',
  'BFL',
];

// Define unit types based on farm type
const FARM_TYPE_UNITS: FarmTypeUnits = {
  "Lechería": "Lt",
  "Genética": "Kg Gen",
  "Ceba": "Kg",
  "Levante": "Kg",
  "Cría": "Kg",
  // Default to "Kg" for any unspecified types
  "default": "Kg"
};

// Define the order of categories within each group
const CATEGORY_ORDER: CategoryOrder = {
  "Compras": ["Costo de ganado en pie", "Cuota de fomento", "Garantia en ganado", "Utilidad en finca - ptz -vt", "Comisiones"],
  "Insumos": ["Sal [ ] Medicamentos", "Combustible", "Maquinaria Pesada", "Construcciones", "Herbicidas", "Herramientas", "Transporte"],
  "Nomina": ["Gastos de Personal", "AIGC"],
  "Otros": ["Otros"]
};

// Función para traducir nombres de meses a español
const traducirMes = (fechaStr: string): string => {
  if (!fechaStr) return '';
  
  // Verificar si el texto ya contiene meses en español
  const mesesEspanol: string[] = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const yaEstaEnEspanol: boolean = mesesEspanol.some(mes => fechaStr.includes(mes));
  
  // Si ya está en español, retornar el texto original
  if (yaEstaEnEspanol) {
    return fechaStr;
  }
  
  const mesesIngles: string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  let resultado: string = fechaStr;
  
  // Reemplazar cada mes en inglés por su equivalente en español
  mesesIngles.forEach((mes: string, index: number) => {
    // Usar word boundaries para evitar reemplazos parciales
    const regex = new RegExp(`\\b${mes}\\b`, 'gi');
    resultado = resultado.replace(regex, mesesEspanol[index]);
  });
  
  return resultado;
};

// Función para extraer solo el nombre del mes sin el año
const extraerSoloMes = (fechaStr: string): string => {
  if (!fechaStr) return '';
  
  const mesesEspanol: string[] = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const mesesIngles: string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  // Buscar si ya está en español
  for (let i = 0; i < mesesEspanol.length; i++) {
    if (fechaStr.includes(mesesEspanol[i])) {
      return mesesEspanol[i];
    }
  }
  
  // Buscar si está en inglés y traducir
  for (let i = 0; i < mesesIngles.length; i++) {
    if (fechaStr.includes(mesesIngles[i])) {
      return mesesEspanol[i];
    }
  }
  
  return fechaStr; // Fallback si no se encuentra el mes
};

const computeRubroYearSummary = (
  historico: FinancialData[] | undefined,
  year: string
): RubroYearSummary[] => {
  if (!historico?.length || !year) return [];

  const yearNum = parseInt(year, 10);
  const mesesDelAnio = historico.filter(
    item => new Date(item.fecha).getFullYear() === yearNum
  );

  return GROUP_ORDER.map(rubro => {
    const ytd = mesesDelAnio.reduce((sum, item) => {
      const valor = item.grupos?.[rubro]?.valor ?? 0;
      return sum + Math.abs(valor);
    }, 0);
    const mesesConDatos = mesesDelAnio.length;
    const promedioMensual = mesesConDatos > 0 ? ytd / mesesConDatos : 0;

    return {
      rubro,
      ytd,
      promedioMensual,
      proyeccionCierre: promedioMensual * 12,
      mesesConDatos,
    };
  });
};

const ProcesoCalculo: React.FC = () => {
  const [financialData, setFinancialData] = useState<FinancialData | null>(null);
  const [fincaInfo, setFincaInfo] = useState<FincaInfo | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('animales');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [calculatorValues, setCalculatorValues] = useState<CalculatorValues>({
    precio: '',
    inventario: '',
    promedio: ''
  });
  const [porcentajeAdicional, setPorcentajeAdicional] = useState<string>('15');
  const [calculationResults, setCalculationResults] = useState<CalculationResults>({
    costoDia: 0,
    produccionNecesaria: 0,
    gastosOperativos: 0,
    utilidadDiaria: 0,
    proyeccionGastosAumentados: 0
  });
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fincaName: string | null = queryParams.get('name');
  const tipo: string | null = queryParams.get('tipo');
  const API_URL: string = process.env.REACT_APP_API_URL as string;
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('6 Meses');
  const [selectedYearForGraphs, setSelectedYearForGraphs] = useState<string>(''); // Para "Por año": año seleccionado en gráficos
  const [selectedRubroSummaryYear, setSelectedRubroSummaryYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<number>(0); // For pie chart, 0 = current (most recent) month
  const [selectedPieYear, setSelectedPieYear] = useState<string>(''); // For pie chart year selection
  const [selectedPieMonthOnly, setSelectedPieMonthOnly] = useState<string>(''); // For pie chart month selection
  const [selectedHistoricalMonth, setSelectedHistoricalMonth] = useState<number>(-1); // For info-table, -1 = will be set to last index
  const [selectedYear, setSelectedYear] = useState<string>(''); // For year selection
  const [selectedMonthOnly, setSelectedMonthOnly] = useState<string>(''); // For month selection
  const [isImagePopupOpen, setIsImagePopupOpen] = useState<boolean>(false);
  
  // Estados para Inventario
  const [hierros, setHierros] = useState<any[]>([]);
  const [inventarioResumen, setInventarioResumen] = useState<any>(null);
  const [inventarioResumenHierro, setInventarioResumenHierro] = useState<any>(null);
  const [mostrarModalInventario, setMostrarModalInventario] = useState<boolean>(false);
  const [hierroSeleccionadoModal, setHierroSeleccionadoModal] = useState<string>('');
  const [loadingModalHierro, setLoadingModalHierro] = useState<boolean>(false);
  const [fechaInicioInventario, setFechaInicioInventario] = useState<string>('');
  const [fechaFinInventario, setFechaFinInventario] = useState<string>('');
  const [inventarioFechaError, setInventarioFechaError] = useState<string | null>(null);
  const [inventarioFiltroMensaje, setInventarioFiltroMensaje] = useState<string | null>(null);

  // Helper function to format numbers
  const formatNumber = (number: number): string => {
    return new Intl.NumberFormat('es-CO', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(number);
  };


  // Helper function to extract unique years from historical data
  const getUniqueYears = (): string[] => {
    if (!historicalData?.historico) return [];
    
    const years = historicalData.historico.map(item => {
      const date = new Date(item.fecha);
      return date.getFullYear().toString();
    });
    
    return Array.from(new Set(years)).sort((a, b) => parseInt(b) - parseInt(a)); // Sort descending (newest first)
  };

  // Helper function to get months for selected year
  const getMonthsForYear = (year: string): Array<{originalIndex: number, monthName: string, fecha: string}> => {
    if (!historicalData?.historico || !year) return [];
    
    return historicalData.historico
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => {
        const date = new Date(item.fecha);
        return date.getFullYear().toString() === year;
      })
      .map(({ item, originalIndex }) => ({
        originalIndex,
        monthName: extraerSoloMes(item.fecha_str), // Solo el nombre del mes, sin el año
        fecha: item.fecha
      }))
      .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()); // Sort by date ascending
  };

  // Funciones específicas para el gráfico de pastel
  const getUniqueYearsForPie = (): string[] => {
    if (!historicalData?.historico) return [];
    
    const years = historicalData.historico.map(item => {
      const date = new Date(item.fecha);
      return date.getFullYear().toString();
    });
    
    return Array.from(new Set(years)).sort((a, b) => parseInt(b) - parseInt(a)); // Sort descending
  };

  const getMonthsForPieYear = (year: string): Array<{originalIndex: number, monthName: string, fecha: string}> => {
    if (!historicalData?.historico || !year) return [];
    
    return historicalData.historico
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => {
        const date = new Date(item.fecha);
        return date.getFullYear().toString() === year;
      })
      .map(({ item, originalIndex }) => ({
        originalIndex,
        monthName: extraerSoloMes(item.fecha_str),
        fecha: item.fecha
      }))
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()); // Sort by date descending
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { id, value } = e.target;
    setCalculatorValues(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handlePorcentajeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPorcentajeAdicional(e.target.value);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const year = e.target.value;
    setSelectedYear(year);
    setSelectedMonthOnly(''); // Reset month when year changes
    setSelectedHistoricalMonth(-1); // Reset historical month selection
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const monthIndex = e.target.value;
    setSelectedMonthOnly(monthIndex);
    if (monthIndex !== '') {
      setSelectedHistoricalMonth(parseInt(monthIndex));
    }
  };

  // Handlers para el gráfico de pastel
  const handlePieYearChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const year = e.target.value;
    setSelectedPieYear(year);
    setSelectedPieMonthOnly(''); // Reset month when year changes
  };

  const handlePieMonthChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const originalIndex = parseInt(e.target.value);
    setSelectedPieMonthOnly(originalIndex.toString());
    setSelectedMonth(originalIndex); // Update the pie chart data
  };

  const handleCalculate = (): void => {
    const currentData = getSelectedHistoricalData();
    if (!currentData || !calculatorValues.precio || !calculatorValues.inventario) {
      alert('Por favor, ingrese todos los valores necesarios');
      return;
    }

    // Usar directamente total_costos y total_gastos en lugar de calcular totalEgresos
    const totalCostos: number = currentData.total_costos || 0;
    const totalGastos: number = currentData.total_gastos || 0;
    
    // Obtener el valor de "Costo de ganado en pie" desde la estructura de datos
    let costoGanadoEnPie: number = 0;
    if (currentData?.grupos?.Compras?.subcategorias?.["Costo de ganado en pie"]) {
      costoGanadoEnPie = currentData.grupos.Compras.subcategorias["Costo de ganado en pie"].valor || 0;
    }

    const { precio, inventario, promedio } = calculatorValues;

    // Calculate individual values first so we can use them for the utilidadDiaria calculation
    const costoDiaValue: number = FINANCIAL_FORMULAS.costoDia(totalCostos, totalGastos, Number(inventario), costoGanadoEnPie);
    const proyeccionGananciaDiariaValue: number = FINANCIAL_FORMULAS.proyeccionGananciaDiaria(Number(promedio), Number(precio));
    const gastosOperativosValue: number = FINANCIAL_FORMULAS.gastosOperativos(totalCostos, totalGastos, costoGanadoEnPie);
    
    setCalculationResults({
      costoDia: costoDiaValue,
      produccionNecesaria: proyeccionGananciaDiariaValue,
      gastosOperativos: gastosOperativosValue,
      utilidadDiaria: FINANCIAL_FORMULAS.utilidadDiaria(proyeccionGananciaDiariaValue, costoDiaValue),
      proyeccionGastosAumentados: FINANCIAL_FORMULAS.proyeccionGastosAumentados(gastosOperativosValue, Number(porcentajeAdicional) || 0)
    });
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        
        // Verificar si es consolidado
        const isConsolidado = tipo === 'consolidado';
        
        // Fetch finca info first
        const fincaResponse = await apiService.get<FincaInfo>('/api/finca_info', {
          params: { name: fincaName }
        });
        
        setFincaInfo(fincaResponse.data);
        
        let financialResponse: any;
        let historicalResponse: any;
        
        if (isConsolidado) {
          // Para consolidado, usar el endpoint de histórico consolidado
          const consolidatedHistoryResponse = await apiService.get<any>('/api/financial_consolidated_history', {
            params: { name: fincaName }
          });
          
          const consolidatedData = consolidatedHistoryResponse.data;
          
          // El histórico consolidado ya viene en el formato correcto
          const historico = consolidatedData.historico_consolidado || [];
          
          // El dato más reciente es el último del array
          const mostRecentData = historico.length > 0 ? historico[historico.length - 1] : null;
          
          if (mostRecentData) {
            financialResponse = { data: mostRecentData };
          } else {
            // Si no hay datos, crear un objeto vacío
            financialResponse = { 
              data: {
                fecha: new Date().toISOString().split('T')[0],
                fecha_str: new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }),
                total_costos: 0,
                total_gastos: 0,
                total_ingresos: 0,
                grupos: {}
              }
            };
          }
          
          // Asignar el histórico completo
          historicalResponse = {
            data: {
              historico: historico
            }
          };
        } else {
          // Para tipos específicos, usar los endpoints normales
          [financialResponse, historicalResponse] = await Promise.all([
            apiService.get<FinancialData>('/api/financial_info', {
              params: {
                name: fincaName,
                tipo: tipo
              }
            }),
            apiService.get<HistoricalData>('/api/financial_history', {
              params: {
                name: fincaName,
                tipo: tipo
              }
            })
          ]);
        }
        
        // Rename "Ingresos" to "Ventas" in financial data
        const financialData: FinancialData = financialResponse.data;
        if (financialData?.grupos?.Ventas?.subcategorias?.Ingresos) {
          // Create a copy of the Ingresos data
          const ingresosData = {...financialData.grupos.Ventas.subcategorias.Ingresos};
          
          // Create new subcategories object without Ingresos but with Ventas
          const newSubcategorias = {...financialData.grupos.Ventas.subcategorias};
          delete newSubcategorias.Ingresos;
          newSubcategorias.Ventas = ingresosData;
          
          // Update the subcategories
          financialData.grupos.Ventas.subcategorias = newSubcategorias;
        }
        
        setFinancialData(financialData);
        
        // Rename "Ingresos" to "Ventas" in historical data
        const historicalData: HistoricalData = historicalResponse.data;
        if (historicalData?.historico) {
          historicalData.historico.forEach(item => {
            if (item?.grupos?.Ventas?.subcategorias?.Ingresos) {
              // Create a copy of the Ingresos data
              const ingresosData = {...item.grupos.Ventas.subcategorias.Ingresos};
              
              // Create new subcategories object without Ingresos but with Ventas
              const newSubcategorias = {...item.grupos.Ventas.subcategorias};
              delete newSubcategorias.Ingresos;
              newSubcategorias.Ventas = ingresosData;
              
              // Update the subcategories
              item.grupos.Ventas.subcategorias = newSubcategorias;
            }
          });
        }
        
        setHistoricalData(historicalData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error al cargar la información. Por favor, intente de nuevo más tarde.');
        setLoading(false);
      }
    };

    if (fincaName) {
      fetchData();
    }
  }, [fincaName, tipo]);

  // Cargar hierros e inventario de la finca
  const DATE_INPUT_REGEX = /^\d{4}-\d{2}-\d{2}$/;

  const handleFechaInicioChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFechaInicioInventario(e.target.value);
  };

  const handleFechaFinChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFechaFinInventario(e.target.value);
  };

  const handleFechaInicioClick = (e: React.MouseEvent<HTMLInputElement>): void => {
    const input = e.currentTarget;
    // Abrir el calendario inmediatamente
    if ('showPicker' in input && typeof (input as any).showPicker === 'function') {
      (input as any).showPicker();
    } else {
      // Fallback: hacer focus para abrir el calendario
      input.focus();
      input.click();
    }
  };

  const handleFechaFinClick = (e: React.MouseEvent<HTMLInputElement>): void => {
    const input = e.currentTarget;
    // Abrir el calendario inmediatamente
    if ('showPicker' in input && typeof (input as any).showPicker === 'function') {
      (input as any).showPicker();
    } else {
      // Fallback: hacer focus para abrir el calendario
      input.focus();
      input.click();
    }
  };

  const validateInventarioDates = useCallback((): boolean => {
    const isValidDateFormat = (value: string): boolean => DATE_INPUT_REGEX.test(value);

    const normalizeDate = (value: string): Date | null => {
      if (!value) return null;
      if (!isValidDateFormat(value)) return null;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      parsed.setHours(0, 0, 0, 0);
      return parsed;
    };

    const startDate = normalizeDate(fechaInicioInventario);
    const endDate = normalizeDate(fechaFinInventario);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (fechaInicioInventario && !isValidDateFormat(fechaInicioInventario)) {
      setInventarioFechaError('Usa el formato AAAA-MM-DD en la fecha inicial.');
      return false;
    }

    if (fechaFinInventario && !isValidDateFormat(fechaFinInventario)) {
      setInventarioFechaError('Usa el formato AAAA-MM-DD en la fecha final.');
      return false;
    }

    if (fechaInicioInventario && !startDate) {
      setInventarioFechaError('La fecha inicial no es válida.');
      return false;
    }

    if (fechaFinInventario && !endDate) {
      setInventarioFechaError('La fecha final no es válida.');
      return false;
    }

    if (startDate && startDate > today) {
      setInventarioFechaError('La fecha inicial no puede estar en el futuro.');
      return false;
    }

    if (endDate && endDate > today) {
      setInventarioFechaError('La fecha final no puede estar en el futuro.');
      return false;
    }

    if (startDate && endDate && startDate > endDate) {
      setInventarioFechaError('La fecha inicial no puede ser posterior a la final.');
      return false;
    }

    setInventarioFechaError(null);
    return true;
  }, [fechaInicioInventario, fechaFinInventario]);

  useEffect(() => {
    const fetchInventarioData = async (): Promise<void> => {
      if (!fincaName) return;

      if (!validateInventarioDates()) {
        setInventarioResumen(null);
        return;
      }
      
      try {
        const filtroActivo = Boolean(fechaInicioInventario || fechaFinInventario);
        setInventarioFiltroMensaje(null);
        // Primero obtener el ID de la finca
        const fincasResponse = await apiService.get<any>('/api/fincas');
        const finca = fincasResponse.data.fincas?.find((f: any) => f.nombre === fincaName);
        
        if (!finca?._id) {
          console.error('No se encontró la finca');
          return;
        }
        
        const fincaId = finca._id;
        
        // Cargar hierros de la finca
        const hierrosResponse = await apiService.get<any>('/api/hierros', {
          params: { finca_id: fincaId, per_page: 100 }
        });
        setHierros(hierrosResponse.data.hierros || []);
        
        // Cargar resumen general de inventario de la finca (sin filtrar por hierro)
        const params: any = { finca_id: fincaId };
        if (fechaInicioInventario) params.fecha_inicio = fechaInicioInventario;
        if (fechaFinInventario) params.fecha_fin = fechaFinInventario;
        
        const resumenResponse = await apiService.get<any>('/api/inventario/resumen', {
          params
        });
        const resumenData = resumenResponse.data;
        const tieneResumen = Array.isArray(resumenData?.resumen) && resumenData.resumen.length > 0;
        const tieneTotales = Boolean(resumenData?.totales_globales?.cantidad);
        const hayDatosInventario = tieneResumen || tieneTotales;

        if (filtroActivo && !hayDatosInventario) {
          setInventarioResumen(null);
          setInventarioFiltroMensaje('En el período seleccionado no hay inventarios registrados.');
          return;
        }

        setInventarioResumen(resumenData);
        setInventarioFechaError(null);
        setInventarioFiltroMensaje(null);
      } catch (err) {
        console.error('Error al cargar datos de inventario:', err);
      }
    };

    if (fincaName) {
      fetchInventarioData();
    }
  }, [fincaName, fechaInicioInventario, fechaFinInventario, validateInventarioDates]);

  // Set default selected year and month when data loads
  useEffect(() => {
    if (historicalData?.historico && historicalData.historico.length > 0) {
      const years = getUniqueYears();
      if (years.length > 0 && !selectedYear) {
        setSelectedYear(years[0]); // Set to most recent year
      }
    }
  }, [historicalData, selectedYear]);

  // Set default selected month when year is selected
  useEffect(() => {
    if (selectedYear && !selectedMonthOnly) {
      const months = getMonthsForYear(selectedYear);
      if (months.length > 0) {
        // Select the most recent month (last in the sorted array)
        const mostRecentMonth = months[months.length - 1];
        setSelectedMonthOnly(mostRecentMonth.originalIndex.toString());
        setSelectedHistoricalMonth(mostRecentMonth.originalIndex);
      }
    }
  }, [selectedYear, selectedMonthOnly]);

  // Set default values for pie chart
  useEffect(() => {
    if (historicalData?.historico && historicalData.historico.length > 0) {
      const pieYears = getUniqueYearsForPie();
      if (pieYears.length > 0 && !selectedPieYear) {
        setSelectedPieYear(pieYears[0]); // Set to most recent year
      }
    }
  }, [historicalData, selectedPieYear]);

  // Set default pie month when pie year is selected
  useEffect(() => {
    if (selectedPieYear && !selectedPieMonthOnly) {
      const pieMonths = getMonthsForPieYear(selectedPieYear);
      if (pieMonths.length > 0) {
        // Select the most recent month (first in the sorted array since it's descending)
        const mostRecentMonth = pieMonths[0];
        setSelectedPieMonthOnly(mostRecentMonth.originalIndex.toString());
        setSelectedMonth(mostRecentMonth.originalIndex);
      }
    }
  }, [selectedPieYear, selectedPieMonthOnly]);

  // Al elegir "Por año", fijar año por defecto (más reciente) si hay datos
  useEffect(() => {
    if (selectedTimeRange !== 'Por año') return;
    const years = getUniqueYears();
    if (years.length > 0 && !selectedYearForGraphs) {
      setSelectedYearForGraphs(years[0]);
    }
  }, [selectedTimeRange, historicalData, selectedYearForGraphs]);

  // Año por defecto para resumen anual por rubro
  useEffect(() => {
    if (!historicalData?.historico?.length) return;
    const years = getUniqueYears();
    if (years.length === 0) return;
    setSelectedRubroSummaryYear(prev =>
      prev && years.includes(prev) ? prev : years[0]
    );
  }, [historicalData, fincaName, tipo]);

  // Actualizar la función filterDataByTimeRange para traducir los meses
  const filterDataByTimeRange = (data: FinancialData[]): FinancialData[] => {
    if (!data || !data.length) return [];
    
    const lastItem = new Date(data[data.length - 1].fecha);
    
    let filteredData: FinancialData[];
    
    switch (selectedTimeRange) {
      case 'Por año':
        if (!selectedYearForGraphs) return [];
        const yearNum = parseInt(selectedYearForGraphs, 10);
        filteredData = data
          .filter(item => new Date(item.fecha).getFullYear() === yearNum)
          .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
        break;
      case 'Mismo mes año anterior':
        const currentMonth = lastItem.getMonth();
        filteredData = data.filter(item => {
          const itemDate = new Date(item.fecha);
          return itemDate.getMonth() === currentMonth && 
                 (itemDate.getFullYear() === lastItem.getFullYear() || 
                  itemDate.getFullYear() === lastItem.getFullYear() - 1);
        });
        break;
      case '3 Meses':
        filteredData = data.slice(-3);
        break;
      case '6 Meses':
        filteredData = data.slice(-6);
        break;
      case '12 Meses':
        filteredData = data.slice(-12);
        break;
      default:
        filteredData = data.slice(-3);
    }
    
    // Traducir los nombres de los meses en fecha_str
    return filteredData.map(item => ({
      ...item,
      fecha_str: traducirMes(item.fecha_str)
    }));
  };

  // Define chart colors for groups
  const groupColors: { [key: string]: string } = {
    "Ventas": '#2E8B57',
    "Compras": '#8B4513',
    "Insumos": '#1E3A5F',
    "Nomina": '#D4A017',
    "Administrativo": '#E67E22',
    "Otros": '#828282'  // Gray color for "Otros" category
  };

  // Helper function to convert hex color to RGB
  const hexToRgb = (hex: string): string => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `${r}, ${g}, ${b}`;
  };

  // Prepare data for bar chart
  const barChartData: ChartData | null = historicalData ? {
    labels: filterDataByTimeRange(historicalData.historico).map(item => item.fecha_str),
    datasets: [
      // First include all the regular groups
      ...GROUP_ORDER.map(groupName => {
        return {
          label: groupName,
          data: filterDataByTimeRange(historicalData.historico).map(item => {
            if (item.grupos && item.grupos[groupName] !== undefined) {
              return item.grupos[groupName].valor || 0;
            }
            return 0; // Return 0 if data doesn't exist
          }),
          backgroundColor: `rgba(${hexToRgb(groupColors[groupName] || '#000000')}, 0.5)`,
          borderColor: groupColors[groupName] || '#000000',
          borderWidth: 1
        };
      }),
      // Then check if "Otros" category should be included
      ...((() => {
        // Check if "Otros" exists and has non-zero values
        const otrosData = filterDataByTimeRange(historicalData.historico).map(item => 
          item.grupos?.Otros?.valor || 0
        );
        
        // Only include if at least one value is non-zero
        if (otrosData.some(value => value !== 0)) {
          return [{
            label: 'Otros',
            data: otrosData,
            backgroundColor: `rgba(${hexToRgb(groupColors["Otros"])}, 0.5)`,
            borderColor: groupColors["Otros"],
            borderWidth: 1
          }];
        }
        return [];
      })())
    ].filter(dataset => 
      // Only include datasets that have at least one non-zero value
      dataset.data.some(value => value !== 0)
    )
  } : null;

  const rubroYearSummary = useMemo(
    () => computeRubroYearSummary(historicalData?.historico, selectedRubroSummaryYear),
    [historicalData, selectedRubroSummaryYear]
  );

  const rubroSummaryDonutData: ChartData | null = useMemo(() => {
    const withData = rubroYearSummary.filter(item => item.ytd > 0);
    if (withData.length === 0) return null;

    return {
      labels: withData.map(item => item.rubro),
      datasets: [{
        data: withData.map(item => item.ytd),
        backgroundColor: withData.map(item => groupColors[item.rubro] || '#828282'),
        borderColor: withData.map(item => groupColors[item.rubro] || '#828282'),
        borderWidth: 1,
      }],
    };
  }, [rubroYearSummary]);

  const rubroDonutItems = useMemo(() => {
    const withData = rubroYearSummary.filter(item => item.ytd > 0);
    const total = withData.reduce((sum, item) => sum + item.ytd, 0);
    if (total === 0) return [];

    return withData.map(item => ({
      rubro: item.rubro,
      ytd: item.ytd,
      color: groupColors[item.rubro] || '#828282',
      porcentaje: (item.ytd / total) * 100,
    }));
  }, [rubroYearSummary]);

  const rubroSummaryDonutOptions: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const rubro = context.label || '';
            const summary = rubroYearSummary.find(item => item.rubro === rubro);
            if (!summary) return '';
            return [
              `Acumulado del año: $${formatNumber(summary.ytd)}`,
              `Promedio mensual: $${formatNumber(summary.promedioMensual)}`,
              `Proyección cierre: $${formatNumber(summary.proyeccionCierre)}`,
              `Meses con datos: ${summary.mesesConDatos}`,
            ];
          },
        },
      },
    },
  }), [rubroYearSummary]);

  // Prepare data for line chart with total_ingresos, total_costos and total_gastos
  const lineChartData: ChartData | null = historicalData ? {
    labels: filterDataByTimeRange(historicalData.historico).map(item => item.fecha_str),
    datasets: [
      {
        label: 'Ingresos Totales',
        data: filterDataByTimeRange(historicalData.historico).map(item => item.total_ingresos || 0),
        borderColor: '#2E8B57', // Green color for income
        backgroundColor: 'rgba(46, 139, 87, 0.1)',
        tension: 0.1,
        borderWidth: 2
      },
      {
        label: 'Costos Totales',
        data: filterDataByTimeRange(historicalData.historico).map(item => item.total_costos || 0),
        borderColor: '#D32F2F', // Red color for costs
        backgroundColor: 'rgba(211, 47, 47, 0.1)',
        tension: 0.1,
        borderWidth: 2
      },
      {
        label: 'Gastos Totales',
        data: filterDataByTimeRange(historicalData.historico).map(item => item.total_gastos || 0),
        borderColor: '#1976D2', // Blue color for expenses
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        tension: 0.1,
        borderWidth: 2
      }
    ]
  } : null;

  // Prepare data for ICE chart (Ingresos Totales / (Costos + Gastos))
  const iceChartData: ChartData | null = historicalData ? {
    labels: filterDataByTimeRange(historicalData.historico).map(item => item.fecha_str),
    datasets: [
      {
        label: 'ICE (Índice de Cobertura de Egresos)',
        data: filterDataByTimeRange(historicalData.historico).map(item => {
          const ingresos = item.total_ingresos || 0;
          const costos = item.total_costos || 0;
          const gastos = item.total_gastos || 0;
          const totalEgresos = costos + gastos;
          
          // Evitar división por cero
          if (totalEgresos === 0) {
            return ingresos > 0 ? 100 : 0; // Si hay ingresos pero no egresos, asignar un valor alto
          }
          
          return (ingresos / totalEgresos);
        }),
        borderColor: '#591c0b', // Primary color for ICE
        backgroundColor: 'rgba(89, 28, 11, 0.1)',
        tension: 0.1,
        borderWidth: 3
      }
    ]
  } : null;

  // Helper function to get financial data for the selected month (for pie chart)
  const getSelectedMonthData = (): FinancialData | null => {
    if (!historicalData?.historico || historicalData.historico.length === 0) {
      return financialData; // Fall back to current data if no historical data
    }
    
    // If we have pie chart year and month selected, use those
    if (selectedPieYear && selectedPieMonthOnly) {
      const pieMonths = getMonthsForPieYear(selectedPieYear);
      const selectedPieMonth = pieMonths.find(month => 
        month.originalIndex.toString() === selectedPieMonthOnly
      );
      
      if (selectedPieMonth) {
        return historicalData.historico[selectedPieMonth.originalIndex];
      }
    }
    
    // Fallback to the old logic if pie chart selection is not complete
    const sortedData = [...historicalData.historico].sort((a, b) => {
      return new Date(b.fecha).getTime() - new Date(a.fecha).getTime();
    });
    
    // If selected month is out of range, return the most recent data
    if (selectedMonth >= sortedData.length) {
      return sortedData[0];
    }
    
    return sortedData[selectedMonth];
  };

  // Helper function to get financial data for the selected historical month (for info table)
  const getSelectedHistoricalData = (): FinancialData | null => {
    if (!historicalData?.historico || historicalData.historico.length === 0) {
      return financialData; // Fall back to current data if no historical data
    }
    
    // Simply return the selected index without sorting
    if (selectedHistoricalMonth >= 0 && selectedHistoricalMonth < historicalData.historico.length) {
      const selectedData = historicalData.historico[selectedHistoricalMonth];
      if (selectedData) {
        return selectedData;
      }
    }
    
    // Fallback to last element (most recent)
    const lastElement = historicalData.historico[historicalData.historico.length - 1];
    if (lastElement) {
      return lastElement;
    }
    
    // Final fallback to financialData
    return financialData;
  };

  // Prepare data for pie chart showing financial breakdown
  const pieChartData: ChartData | null = useMemo(() => {
    const data = getSelectedMonthData();
    
    if (!data) return null;
    
    const total_ingresos = data.total_ingresos || 0;
    const total_costos = data.total_costos || 0;
    const total_gastos = data.total_gastos || 0;
    
    // Calculate utilidad (profit/loss)
    const utilidad = total_ingresos - (total_gastos + total_costos);
    
    // Set color based on whether utilidad is positive or negative
    const utilidadColor = utilidad >= 0 ? '#6BAD88' : '#D32F2F';
    
    return {
      labels: ['Costos', 'Gastos', utilidad >= 0 ? 'Desempeño favorable' : 'Desempeño adverso'],
      datasets: [
        {
          data: [
            Math.abs(total_costos), 
            Math.abs(total_gastos), 
            Math.abs(utilidad)
          ],
          backgroundColor: [
            '#ECA362',  // Updated color for costs
            '#60748E',  // Updated color for expenses
            utilidadColor // Green for profit, Red for loss
          ],
          borderColor: [
            'rgba(236, 163, 98, 0.5)',  // Matching border for costs
            'rgba(96, 116, 142, 0.5)',   // Matching border for expenses
            utilidad >= 0 ? 'rgba(107, 173, 136, 0.5)' : 'rgba(211, 47, 47, 0.5)' // Matching border for profit/loss
          ],
          borderWidth: 1,
        },
      ],
    };
  }, [selectedPieYear, selectedPieMonthOnly, historicalData]);

  const pieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed !== null) {
              label += '$' + formatNumber(context.parsed);
            }
            return label;
          }
        }
      }
    }
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: number) {
            return '$' + formatNumber(value);
          }
        }
      }
    }
  };

  const iceChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2);
              if (context.parsed.y >= 1) {
                label += ' (Cobertura positiva)';
              } else {
                label += ' (Cobertura insuficiente)';
              }
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: number) {
            return value.toFixed(2);
          }
        },
        grid: {
          color: function(context: any) {
            // Línea horizontal en y=1 para indicar el punto de equilibrio
            return context.tick.value === 1 ? '#2e7d32' : 'rgba(0,0,0,0.1)';
          }
        }
      }
    }
  };

  // Plugin para renderizar etiquetas dentro de las barras y bordes estilo boton4
  const inventarioButtonPlugin = {
    id: 'inventarioButtonPlugin',
    afterDatasetsDraw: (chart: any) => {
      const ctx = chart.ctx;
      const yScale = chart.scales.y;
      
      if (!yScale || !yScale.ticks || !chart.data || !chart.data.labels) return;
      
      // Obtener el meta del dataset para acceder a las barras
      const meta = chart.getDatasetMeta(0);
      if (!meta || !meta.data) return;
      
      yScale.ticks.forEach((tick: any, index: number) => {
        const label: string = chart.data.labels[index] || '';
        if (!label) return;
        
        // Obtener la barra correspondiente
        const bar = meta.data[index];
        if (!bar) return;
        
        // En un gráfico horizontal, bar.x es donde termina la barra, bar.width es el ancho
        const barWidth = bar.width;
        const barHeight = bar.height;
        const barY = bar.y; // Centro vertical de la barra
        const barX = bar.x; // Posición X del final de la barra
        const barLeft = chart.chartArea.left; // Inicio de la barra
        const barTop = barY - barHeight / 2;
        const barBottom = barY + barHeight / 2;
        
        ctx.save();
        
        // Dibujar bordes estilo boton4: amarillo en bottom y right, blanco en top y left
        ctx.lineWidth = 2;
        
        // Borde superior (blanco)
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(barLeft, barTop);
        ctx.lineTo(barX, barTop);
        ctx.stroke();
        
        // Borde izquierdo (blanco)
        ctx.beginPath();
        ctx.moveTo(barLeft, barTop);
        ctx.lineTo(barLeft, barBottom);
        ctx.stroke();
        
        // Borde inferior (amarillo - color-accent-yellow)
        ctx.strokeStyle = '#ffb300';
        ctx.beginPath();
        ctx.moveTo(barLeft, barBottom);
        ctx.lineTo(barX, barBottom);
        ctx.stroke();
        
        // Borde derecho (amarillo - color-accent-yellow)
        ctx.beginPath();
        ctx.moveTo(barX, barTop);
        ctx.lineTo(barX, barBottom);
        ctx.stroke();
        
        // Calcular posición del texto
        // Si la barra es muy pequeña, mostrar el texto fuera (a la derecha)
        // Si es lo suficientemente grande, mostrarlo dentro (centrado)
        const isBarTooSmall = barWidth < 50;
        const textX = isBarTooSmall 
          ? barX + 8 // Fuera de la barra, a la derecha con un pequeño margen
          : barLeft + (barWidth / 2); // Dentro de la barra, centrado
        const textY = barY;
        
        // Configurar fuente y estilo según boton4
        ctx.font = '700 10px Rubik, sans-serif'; // font-weight 700 como boton4
        ctx.textAlign = isBarTooSmall ? 'left' : 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#591c0b'; // Color primario como boton4
        
        // Si la barra es pequeña, mostrar el texto completo en una línea
        // Si es grande, dividir en dos líneas si es necesario
        let displayLabel = label;
        if (!isBarTooSmall && label.length > 6) {
          const mitad = Math.ceil(label.length / 2);
          displayLabel = label.substring(0, mitad) + '\n' + label.substring(mitad);
        }
        const lines = displayLabel.split('\n');
        
        const lineHeight = 11;
        const startY = textY - (lines.length - 1) * lineHeight / 2;
        
        // Dibujar fondo blanco detrás del texto si está fuera de la barra para mejor legibilidad
        if (isBarTooSmall) {
          ctx.save();
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          const textWidth = ctx.measureText(label).width;
          const padding = 4;
          ctx.fillRect(
            textX - padding,
            textY - lineHeight / 2 - padding,
            textWidth + (padding * 2),
            lineHeight + (padding * 2)
          );
          ctx.restore();
          ctx.fillStyle = '#591c0b'; // Restaurar color del texto
        }
        
        lines.forEach((line: string, lineIndex: number) => {
          ctx.fillText(
            line.trim(),
            textX,
            startY + lineIndex * lineHeight
          );
        });
        
        ctx.restore();
      });
    }
  };

  // Preparar datos para el gráfico de barras de inventario general
  const inventarioBarChartData: ChartData | null = useMemo(() => {
    if (!inventarioResumen?.resumen || !Array.isArray(inventarioResumen.resumen)) return null;
    
    // Agrupar todas las categorías de todos los hierros
    const categoriasMap: { [key: string]: { codigo: string; nombre: string; total: number } } = {};
    
    inventarioResumen.resumen.forEach((hierro: any) => {
      if (hierro.categorias && Array.isArray(hierro.categorias)) {
        hierro.categorias.forEach((cat: any) => {
          if (categoriasMap[cat.codigo]) {
            categoriasMap[cat.codigo].total += cat.total || 0;
          } else {
            categoriasMap[cat.codigo] = {
              codigo: cat.codigo,
              nombre: cat.nombre,
              total: cat.total || 0
            };
          }
        });
      }
    });
    
    const codigoOrderIndex = new Map(
      INVENTARIO_CATEGORIA_CODIGO_ORDER.map((codigo, index) => [codigo, index])
    );
    const categorias = Object.values(categoriasMap).sort((a, b) => {
      const ia = codigoOrderIndex.get(a.codigo);
      const ib = codigoOrderIndex.get(b.codigo);
      if (ia !== undefined && ib !== undefined) return ia - ib;
      if (ia !== undefined) return -1;
      if (ib !== undefined) return 1;
      return a.codigo.localeCompare(b.codigo);
    });
    
    if (categorias.length === 0) return null;
    
    return {
      labels: categorias.map(cat => cat.codigo), // Solo las siglas
      datasets: [
        {
          label: 'Cantidad de Ganado',
          data: categorias.map(cat => cat.total),
          backgroundColor: 'rgba(218, 165, 32, 0.197)', // Fondo dorado con transparencia
          borderColor: 'transparent', // Transparente porque dibujamos bordes personalizados en el plugin
          borderWidth: 0, // Sin borde por defecto, se dibujan en el plugin
          borderRadius: 8 // Bordes redondeados 0.5rem (8px) como boton4
        }
      ]
    };
  }, [inventarioResumen]);

  // Calcular el total de inventario de la finca
  const totalInventarioFinca: number = useMemo(() => {
    if (!inventarioResumen) return 0;
    
    // Usar totales_globales si está disponible
    if (inventarioResumen.totales_globales?.cantidad) {
      return inventarioResumen.totales_globales.cantidad;
    }
    
    // Si no, calcular sumando todas las categorías de todos los hierros
    if (inventarioResumen.resumen && Array.isArray(inventarioResumen.resumen)) {
      return inventarioResumen.resumen.reduce((total: number, hierro: any) => {
        if (hierro.categorias && Array.isArray(hierro.categorias)) {
          const hierroTotal = hierro.categorias.reduce((sum: number, cat: any) => sum + (cat.total || 0), 0);
          return total + hierroTotal;
        }
        return total + (hierro.total_general || 0);
      }, 0);
    }
    
    return 0;
  }, [inventarioResumen]);

  // Opciones para el gráfico horizontal de inventario
  const inventarioBarChartOptions: any = useMemo(() => ({
    indexAxis: 'y', // Hace el gráfico horizontal
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 5,
        bottom: 5
      }
    },
    plugins: {
      legend: {
        display: false // Ocultar leyenda
      },
      tooltip: {
        backgroundColor: 'rgba(89, 28, 11, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#591c0b',
        borderWidth: 1,
        padding: 10,
        titleFont: {
          family: 'Rubik, sans-serif',
          size: 12,
          weight: '600'
        },
        bodyFont: {
          family: 'Rubik, sans-serif',
          size: 11
        },
        callbacks: {
          label: function(context: any) {
            return `Cantidad: ${formatNumber(context.parsed.x)}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: function(value: number) {
            // Mostrar valores enteros sin decimales
            if (Number.isInteger(value)) {
              return formatNumber(value);
            }
            return '';
          },
          stepSize: 1,
          font: {
            size: 10,
            weight: 'normal',
            family: 'Rubik, sans-serif'
          },
          color: '#333333'
        },
        title: {
          display: false
        },
        grid: {
          color: 'rgba(89, 28, 11, 0.1)',
          lineWidth: 1
        }
      },
      y: {
        ticks: {
          display: false // Ocultar las etiquetas originales (se mostrarán dentro de las barras)
        },
        grid: {
          display: false // Ocultar líneas de cuadrícula en el eje Y
        }
      }
    }
  }), [inventarioBarChartData]);

  // Función para obtener las categorías por grupo
  const getCategoriesByGroup = (group: string): Array<[string, any]> => {
    const currentData = getSelectedHistoricalData();
    if (!currentData?.grupos?.[group]?.subcategorias) {
      return [];
    }
    
    // Convert subcategories object to array of [name, value] entries
    const categories: Array<[string, any]> = Object.entries(currentData.grupos[group].subcategorias)
      .map(([name, data]) => [name, data] as [string, any]);
    
    // Sort categories if they have a defined order
    if (CATEGORY_ORDER[group]) {
      return categories.sort(([nameA], [nameB]) => {
        const indexA = CATEGORY_ORDER[group].indexOf(nameA);
        const indexB = CATEGORY_ORDER[group].indexOf(nameB);
        
        // If both categories are in the predefined order, sort by that order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        // If only one is in the order, prioritize it
        else if (indexA !== -1) {
          return -1;
        }
        else if (indexB !== -1) {
          return 1;
        }
        // Otherwise, maintain original order
        return 0;
      });
    }
    
    return categories;
  };

  // Función para manejar la expansión de grupos
  const handleGroupToggle = (group: string): void => {
    setExpandedGroup(expandedGroup === group ? null : group);
  };

  // Función para renderizar una fila de categoría
  const renderCategoryRow = (categoryName: string, categoryValue: number, isInGroup: boolean = false, isOtrosCategory: boolean = false, categoryData: any = null): React.ReactElement => {
    return (
      <tr key={categoryName} className={`table-row ${isInGroup ? 'category-row' : 'single-category-row'}`}>
        <td className="concept rubik600x35 category-concept sub-category">{categoryName}:</td>
        <td className="currency sub-category">$</td>
        <td className="value sub-category">{formatNumber(categoryValue)}</td>
      </tr>
    );
  };

  // Función para renderizar los detalles de la categoría "Otros"
  const renderOtrosDetails = (categoryData: any): React.ReactElement[] | null => {
    if (!categoryData || !categoryData.detalles) {
      return null;
    }

    return categoryData.detalles.map((detalle: any, index: number) => (
      <tr key={`${detalle.codigo}-${index}`} className="table-row category-row">
        <td className="concept rubik600x35 category-concept sub-category">{detalle.descripcion} ({detalle.codigo}):</td>
        <td className="currency sub-category">$</td>
        <td className="value sub-category">{formatNumber(detalle.valor)}</td>
      </tr>
    ));
  };

  // Función para abrir modal de inventario de un hierro
  const handleHierroClick = async (hierroId: string, hierroNombre: string): Promise<void> => {
    try {
      if (!fincaName) return;
      
      // Activar loading
      setLoadingModalHierro(true);
      
      // Obtener el ID de la finca
      const fincasResponse = await apiService.get<any>('/api/fincas');
      const finca = fincasResponse.data.fincas?.find((f: any) => f.nombre === fincaName);
      
      if (!finca?._id) {
        setError('No se encontró la finca');
        setLoadingModalHierro(false);
        return;
      }
      
      setHierroSeleccionadoModal(hierroNombre);
      
      const params: any = { finca_id: finca._id, hierro_id: hierroId };
      if (fechaInicioInventario) params.fecha_inicio = fechaInicioInventario;
      if (fechaFinInventario) params.fecha_fin = fechaFinInventario;
      
      const response = await apiService.get<any>('/api/inventario/resumen', {
        params
      });
      setInventarioResumenHierro(response.data);
      setMostrarModalInventario(true);
    } catch (err) {
      console.error('Error al cargar resumen del hierro:', err);
      setError('Error al cargar el resumen del inventario');
    } finally {
      // Desactivar loading siempre, incluso si hay error
      setLoadingModalHierro(false);
    }
  };

  // Función para renderizar un grupo y sus categorías
  const renderGroup = (groupName: string, groupData: any): React.ReactElement => {
    const categories = getCategoriesByGroup(groupName);
    const isExpanded = expandedGroup === groupName;
    const hasMultipleCategories = categories.length > 1;

    return (
      <React.Fragment key={groupName}>
        {hasMultipleCategories ? (
          <>
            <tr className="table-row group-row" onClick={() => handleGroupToggle(groupName)}>
              <td className="concept rubik600x35 group-concept">
                <span className="collapse-indicator">{isExpanded ? '▼' : '▶'}</span>
                {groupName}
              </td>
              <td className="currency">$</td>
              <td className="value">{formatNumber(groupData.valor)}</td>
            </tr>
            {isExpanded && categories.map(([categoryName, category]) => 
              renderCategoryRow(categoryName, category.valor, true)
            )}
          </>
        ) : (
          categories.map(([categoryName, category]) => {
            return (
              <tr key={categoryName} className="table-row single-category-row">
                <td className="concept rubik600x35 group-concept">{categoryName}:</td>
                <td className="currency">$</td>
                <td className="value">{formatNumber(category.valor)}</td>
              </tr>
            );
          })
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="proceso-calculo-container10">
      <Helmet>
        <title>ProcesoCalculo - ProyectoGanando</title>
        <meta property="og:title" content="ProcesoCalculo - ProyectoGanando" />
      </Helmet>
      <div className="proceso-calculo-container11">
        <div className="proceso-calculo-container12 dashboard-container">
          <div className="dashboard-header">
            {/* Sección 1: Imagen */}
            <div className="dashboard-image-container" onClick={() => setIsImagePopupOpen(true)} style={{ cursor: 'pointer' }}>
              {fincaInfo?.imagen && (
                <img
                  src={`${API_URL}/api/images/${fincaInfo.imagen}`}
                  alt={fincaName || ''}
                />
              )}
            </div>
            
            {/* Sección 2: Información Principal */}
            <div className="proceso-calculo-container13 info-value" style={{ 
              flex: '1', 
              textAlign: 'center' 
            }}>
              <h1 className="proceso-calculo-text10">
                {fincaName} ({tipo === 'consolidado' ? 'Consolidado Finca' : tipo})
              </h1>
              <h2 className="proceso-calculo-text11">
                {(() => {
                  if (!historicalData?.historico || historicalData.historico.length === 0) {
                    return tipo === 'consolidado' ? 'Vista Consolidada' : 'Última actualización';
                  }
                  
                  if (tipo === 'consolidado') {
                    return 'Vista Consolidada';
                  }
                  
                  // Find the index of the most recent date in the original array
                  const sortedWithIndex = historicalData.historico
                    .map((item, originalIndex) => ({ item, originalIndex }))
                    .sort((a, b) => new Date(a.item.fecha).getTime() - new Date(b.item.fecha).getTime()); // CHANGED: Inverted sort order
                  
                  // Check if we're viewing the most recent element (LAST in this sort order)
                  const mostRecentOriginalIndex = sortedWithIndex.length > 0 ? sortedWithIndex[sortedWithIndex.length - 1].originalIndex : -1;
                  const isViewingMostRecent = selectedHistoricalMonth >= 0 && 
                    selectedHistoricalMonth === mostRecentOriginalIndex;
                  
                  return isViewingMostRecent ? 'Última actualización' : 'Datos Históricos';
                })()}
              </h2>
              <h3 className="proceso-calculo-text12">
                {(() => {
                  if (!historicalData?.historico || historicalData.historico.length === 0) {
                    return financialData ? traducirMes(financialData.fecha_str) : 'Cargando...';
                  }
                  
                  // Simply get the selected data without sorting
                  if (selectedHistoricalMonth >= 0 && selectedHistoricalMonth < historicalData.historico.length) {
                    const selectedData = historicalData.historico[selectedHistoricalMonth];
                    return selectedData ? traducirMes(selectedData.fecha_str) : 'Fecha no disponible';
                  }
                  
                  // Fallback to last element or financialData
                  const lastElement = historicalData.historico[historicalData.historico.length - 1];
                  return lastElement ? traducirMes(lastElement.fecha_str) : (financialData ? traducirMes(financialData.fecha_str) : 'Cargando...');
                })()}
              </h3>
            </div>
            
            {/* Sección 3: Selector de Período */}
            <div className="historical-month-selector">
              <label className="rubik600x35" style={{ 
                color: 'var(--dl-color-ganando-botones)', 
                fontSize: '16px' 
              }}>
                Seleccione el período:
              </label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {/* Selector de Año */}
                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  className="rubik600x35"
                  style={{ 
                    padding: '8px 12px', 
                    borderRadius: '6px', 
                    border: '2px solid var(--dl-color-ganando-botones)', 
                    backgroundColor: 'white',
                    color: 'var(--dl-color-ganando-botones)',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    minWidth: '80px'
                  }}
                >
                  <option value="">Año</option>
                  {getUniqueYears().map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                
                {/* Selector de Mes */}
                <select
                  value={selectedMonthOnly}
                  onChange={handleMonthChange}
                  className="rubik600x35"
                  disabled={!selectedYear}
                  style={{ 
                    padding: '8px 12px', 
                    borderRadius: '6px', 
                    border: '2px solid var(--dl-color-ganando-botones)', 
                    backgroundColor: selectedYear ? 'white' : '#f5f5f5',
                    color: selectedYear ? 'var(--dl-color-ganando-botones)' : '#999',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    minWidth: '120px',
                    cursor: selectedYear ? 'pointer' : 'not-allowed'
                  }}
                >
                  <option value="">Mes</option>
                  {selectedYear && getMonthsForYear(selectedYear).map(({ originalIndex, monthName }) => (
                    <option key={originalIndex} value={originalIndex}>
                      {monthName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Grid de 3 columnas */}
          <div className="dashboard-grid-container">
            {/* Primer Grid - Inputs Container */}
            <div className="proceso-calculo-container37 inputs-container">
              <div className="input-group">
                <label
                  htmlFor="precio"
                  className="proceso-calculo-text20 rubik600x35"
                >
                  Precio Mercado:
                </label>
                <div className="proceso-calculo-container39 input-wrapper">
                  <input
                    type="number"
                    id="precio"
                    placeholder="0.00"
                    value={calculatorValues.precio}
                    onChange={handleInputChange}
                    className="proceso-calculo-textinput1 rubik600x35"
                  />
                </div>
              </div>
              <div className="proceso-calculo-container40 input-group">
                <label
                  htmlFor="inventario"
                  className="proceso-calculo-text22 rubik600x35"
                >
                  cantidad de animales:
                </label>
                <div className="proceso-calculo-container41 input-wrapper">
                  <input
                    type="number"
                    id="inventario"
                    placeholder="0"
                    value={calculatorValues.inventario}
                    onChange={handleInputChange}
                    className="proceso-calculo-textinput2 rubik600x35"
                  />
                </div>
              </div>
              <div className="proceso-calculo-container42 input-group">
                <label
                  htmlFor="promedio"
                  className="proceso-calculo-text23 rubik600x35"
                >
                  Promedio produccion día:
                </label>
                <div className="proceso-calculo-container43 input-wrapper">
                  <input
                    type="number"
                    id="promedio"
                    placeholder="0.00"
                    value={calculatorValues.promedio}
                    onChange={handleInputChange}
                    className="proceso-calculo-textinput3 rubik600x35"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleCalculate}
                className="proceso-calculo-button boton3d-style"
                style={{
                  '--finca-image-url': fincaInfo?.imagen ? `url('${API_URL}/api/images/${fincaInfo.imagen}')` : 'none'
                } as React.CSSProperties}
              >
                CALCULAR TOTALES
              </button>
            </div>
            
            
            {/* Segundo Grid - Results Container */}
            <div className="proceso-calculo-container44 results-container">
              <div className="proceso-calculo-container45 result-card">
                <div className="proceso-calculo-container46 result-item">
                  <h3 className="proceso-calculo-text24 result-title rubik600x35">
                    Costo día de sostenimiento por animales:
                  </h3>
                  <div className="proceso-calculo-container47 result-value">
                    <span className="proceso-calculo-text25 currency ingrMenu">
                      $
                    </span>
                    <span
                      id="costo-dia"
                      className="proceso-calculo-text26 rubik600x35 value"
                    >
                      {formatNumber(calculationResults.costoDia)}
                    </span>
                  </div>
                </div>
                <div className="proceso-calculo-container48 production-info result-item">
                  <h3 className="proceso-calculo-text27 rubik600x35 result-title">
                    Proyección ganancia diaria por kilogramo:
                  </h3>
                  <div className="result-complex">
                    <div className="proceso-calculo-container50 result-value">
                      <span className="proceso-calculo-text29 currency ingrMenu">
                        $
                      </span>
                      <span
                        id="produccion-necesaria"
                        className="proceso-calculo-text28 rubik600x35 value"
                      >
                        {formatNumber(calculationResults.produccionNecesaria)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Nuevo resultado - Utilidad Neta */}
                <div className="proceso-calculo-container52 production-info result-item">
                  <h3 className="proceso-calculo-text33 rubik600x35 result-title">
                    Utilidad neta:
                  </h3>
                  <div className="result-complex">
                    <div className="proceso-calculo-container59 result-value">
                      <span className="proceso-calculo-text34 currency ingrMenu">
                        $
                      </span>
                      <span
                        id="utilidad-neta"
                        className="proceso-calculo-text35 rubik600x35 value"
                      >
                        {formatNumber(calculationResults.utilidadDiaria)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="proceso-calculo-container51 production-info result-item">
                  <h3 className="proceso-calculo-text30 rubik600x35">
                     presupuesto gastos operativos mes:
                  </h3>
                  <div className="result-complex">
                    <div className="proceso-calculo-container53 result-value">
                      <span className="proceso-calculo-text31 currency ingrMenu">
                        $
                      </span>
                      <span
                        id="gastos-operativos"
                        className="proceso-calculo-text32 rubik600x35 value"
                      >
                        {formatNumber(calculationResults.gastosOperativos)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Nuevo resultado - Porcentaje Adicional */}
                <div className="proceso-calculo-container55 production-info result-item">
                  <div className="porcentaje-adicional-inline">
                    <h3 className="proceso-calculo-text36 rubik600x35 result-title">
                      Porcentaje adicional:
                    </h3>
                    <div className="result-complex-inline">
                      <span className="proceso-calculo-text37 currency ingrMenu">
                        %
                      </span>
                      <input
                        type="number"
                        id="porcentaje-adicional"
                        placeholder="0"
                        min="0"
                        max="100"
                        value={porcentajeAdicional}
                        onChange={handlePorcentajeChange}
                        className="proceso-calculo-textinput4 rubik600x35 value-input no-spinner"
                        style={{
                          width: '60px',
                          padding: '4px 6px',
                          borderRadius: '4px',
                          border: '1px solid var(--dl-color-ganando-botones)',
                          backgroundColor: 'white',
                          color: 'var(--dl-color-ganando-botones)',
                          fontSize: 'inherit',
                          textAlign: 'center',
                          appearance: 'textfield',
                          MozAppearance: 'textfield',
                          WebkitAppearance: 'none'
                        } as React.CSSProperties & {
                          appearance?: string;
                          MozAppearance?: string;
                          WebkitAppearance?: string;
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Nuevo resultado - Proyección Aumentada */}
                <div className="proceso-calculo-container57 production-info result-item">
                  <h3 className="proceso-calculo-text38 rubik600x35 result-title">
                    Proyección Aumentada:
                  </h3>
                  <div className="result-complex">
                    <div className="proceso-calculo-container58 result-value">
                      <span className="proceso-calculo-text39 currency ingrMenu">
                        $
                      </span>
                      <span
                        id="proyeccion-aumentada"
                        className="proceso-calculo-text40 rubik600x35 value"
                      >
                        {formatNumber(calculationResults.proyeccionGastosAumentados)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tercer Grid - Info Section */}
            <div className="proceso-calculo-container17 info-section VistaMovilTitulo">
            <InfoProcesos 
                data={getSelectedHistoricalData()} 
                loading={loading} 
                error={error} 
              /> 
            </div>
          </div>
        </div>
        
        {/* Financial Breakdown Pie Chart y Graphs lado a lado */}
        <div className="dashboard-charts-container">
          {/* Contenedor para Pie Chart e Inventario (en columna) */}
          <div className="pie-chart-inventario-wrapper">
            {/* Pie Chart */}
            <div className="proceso-calculo-container-pie-chart dashboard-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 className="rubik600x35" style={{color: 'var(--dl-color-ganando-botones)', margin: '0', fontSize: '15px'}}>Distribución Financiera</h3>
            <div className="month-selector" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {/* Selector de Año para Pie Chart */}
              <select
                value={selectedPieYear}
                onChange={handlePieYearChange}
                className="rubik600x35"
                style={{
                  padding: '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  minWidth: '80px'
                }}
              >
                <option value="">Año</option>
                {getUniqueYearsForPie().map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              {/* Selector de Mes para Pie Chart */}
              <select
                value={selectedPieMonthOnly}
                onChange={handlePieMonthChange}
                className="rubik600x35"
                disabled={!selectedPieYear}
                style={{
                  padding: '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  minWidth: '120px',
                  backgroundColor: selectedPieYear ? 'white' : '#f5f5f5',
                  color: selectedPieYear ? 'var(--dl-color-ganando-botones)' : '#999',
                  cursor: selectedPieYear ? 'pointer' : 'not-allowed'
                }}
              >
                <option value="">Mes</option>
                {selectedPieYear && getMonthsForPieYear(selectedPieYear).map(({ originalIndex, monthName }) => (
                  <option key={originalIndex} value={originalIndex}>
                    {monthName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="graph-wrapper">
            {pieChartData && (
              <Pie data={pieChartData} options={pieChartOptions} height={300} />
            )}
          </div>
          {pieChartData && (
            <div className="financial-summary">
              {(() => {
                const data = getSelectedMonthData();
                if (!data) return null;
                
                const total_ingresos = data.total_ingresos || 0;
                const total_costos = data.total_costos || 0;
                const total_gastos = data.total_gastos || 0;
                const utilidad = total_ingresos - (total_gastos + total_costos);
                const isPositive = utilidad >= 0;
                
                return (
                  <>
                    <p className="rubik600x35">
                      <span>Ingresos Totales: </span>
                      <span className="currency">$</span>
                      <span className="value">{formatNumber(total_ingresos)}</span>
                    </p>
                    <p className="rubik600x35">
                      <span>Costos Totales: </span>
                      <span className="currency">$</span>
                      <span className="value">{formatNumber(total_costos)}</span>
                    </p>
                    <p className="rubik600x35">
                      <span>Gastos Totales: </span>
                      <span className="currency">$</span>
                      <span className="value">{formatNumber(total_gastos)}</span>
                    </p>
                    <p className={`rubik600x35 ${isPositive ? 'positive-value' : 'negative-value'}`}>
                      <span>{isPositive ? 'Desempeño favorable: ' : 'Desempeño adverso: '}</span>
                      <span className="currency">$</span>
                      <span className="value">{formatNumber(Math.abs(utilidad))}</span>
                    </p>
                  </>
                );
              })()}
            </div>
          )}
        </div>
        
        {/* Sección de Inventario */}
        <div className="proceso-calculo-container-inventario dashboard-container">
          <h3 className="rubik600x35" style={{color: 'var(--dl-color-ganando-botones)', margin: '0 0 1rem 0', fontSize: '15px'}}>
            Total inventario finca: {formatNumber(totalInventarioFinca)}
          </h3>
          
          {/* Selector de fechas para inventario */}
          <div className="inventario-date-selector" style={{ marginBottom: '1.5rem' }}>
            <div className="inventario-date-row">
              <div className="date-input-wrapper inventario-date-input">
                <input
                  type="date"
                  className="filter-input date-input"
                  value={fechaInicioInventario}
                  onChange={handleFechaInicioChange}
                  onClick={handleFechaInicioClick}
                  max={new Date().toISOString().split('T')[0]}
                />
                {!fechaInicioInventario && (
                  <span className="date-placeholder">Fecha inicial</span>
                )}
              </div>
              <span className="date-separator">a</span>
              <div className="date-input-wrapper inventario-date-input">
                <input
                  type="date"
                  className="filter-input date-input"
                  value={fechaFinInventario}
                  onChange={handleFechaFinChange}
                  onClick={handleFechaFinClick}
                  max={new Date().toISOString().split('T')[0]}
                />
                {!fechaFinInventario && (
                  <span className="date-placeholder">Fecha final</span>
                )}
              </div>
            </div>
              {inventarioFechaError && (
                <p className="inventario-date-error">{inventarioFechaError}</p>
              )}
              {!inventarioFechaError && inventarioFiltroMensaje && (
                <p className="inventario-date-info">{inventarioFiltroMensaje}</p>
              )}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
              <button
                className="button"
                onClick={() => {
                  setFechaInicioInventario('');
                  setFechaFinInventario('');
                  setInventarioFechaError(null);
                  setInventarioFiltroMensaje(null);
                }}
                style={{ padding: '8px 16px', fontSize: '14px' }}
              >
                Limpiar
              </button>
            </div>
          </div>
          
          {/* Imágenes de hierros */}
          <div className="hierros-inventario-grid">
            {hierros.map((hierro) => {
              const imagenUrl = hierro.imagen_url || hierro.imagen;
              const fullImageUrl = imagenUrl 
                ? (imagenUrl.startsWith('http') 
                    ? imagenUrl 
                    : `${API_URL}${imagenUrl.startsWith('/') ? imagenUrl : `/${imagenUrl}`}`)
                : null;
              
              return (
                <div
                  key={hierro._id}
                  className="hierro-inventario-item"
                  onClick={() => handleHierroClick(hierro._id, hierro.nombre)}
                  style={{ cursor: 'pointer' }}
                >
                  {fullImageUrl ? (
                    <img
                      src={fullImageUrl}
                      alt={hierro.nombre}
                      className="hierro-inventario-image"
                    />
                  ) : (
                    <div className="hierro-inventario-placeholder">
                      🏷️
                    </div>
                  )}
                  <span className="hierro-inventario-nombre">{hierro.nombre}</span>
                </div>
              );
            })}
          </div>
          
          {/* Gráfico de barras de inventario general */}
          {inventarioBarChartData && (
            <div className="inventario-bar-chart-container">
              <div className="graph-wrapper">
                <Bar 
                  data={inventarioBarChartData} 
                  options={inventarioBarChartOptions} 
                  plugins={[inventarioButtonPlugin]}
                  height={300} 
                />
              </div>
            </div>
          )}
        </div>
          </div>
        
        {/* Modal de resumen de inventario por hierro */}
        {mostrarModalInventario && inventarioResumenHierro && (
          <div 
            className="modal-overlay-inventario"
            onClick={() => setMostrarModalInventario(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
          >
            <div 
              className="modal-content-inventario"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '25px',
                maxWidth: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative'
              }}
            >
              <button
                onClick={() => setMostrarModalInventario(false)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#666',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
              
              <h2 style={{ margin: '0 0 20px 0', color: 'var(--dl-color-ganando-botones)' }}>
                Resumen de Inventario - {hierroSeleccionadoModal}
              </h2>
              
              {inventarioResumenHierro.resumen && inventarioResumenHierro.resumen.length > 0 ? (
                inventarioResumenHierro.resumen.map((hierro: any, index: number) => (
                  <div key={index} className="resumen-hierro-card" style={{ marginBottom: '20px' }}>
                    <div className="resumen-hierro-header">
                      <h3 style={{ margin: 0 }}>{hierro.hierro}</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                        <span className="resumen-total">Total: {hierro.total_general}</span>
                        {hierro.valor_total !== undefined && hierro.valor_total > 0 && (
                          <span className="resumen-total" style={{ color: '#4caf50', fontWeight: '600' }}>
                            Valor Total: ${hierro.valor_total.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        )}
                        {hierro.peso_total !== undefined && hierro.peso_total > 0 && (
                          <span className="resumen-total" style={{ color: '#2196F3', fontWeight: '600' }}>
                            Peso Total: {hierro.peso_total.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="resumen-categorias-grid">
                      {hierro.categorias && hierro.categorias.map((cat: any, catIndex: number) => (
                        <div key={catIndex} className="resumen-categoria-card">
                          <div className="resumen-categoria-header">
                            <span className="resumen-categoria-codigo">{cat.codigo}</span>
                            <span className="resumen-categoria-nombre">{cat.nombre}</span>
                          </div>
                          <div className="resumen-categoria-stats">
                            <div className="resumen-stat">
                              <span className="resumen-stat-label">Total:</span>
                              <span className="resumen-stat-value">{cat.total}</span>
                            </div>
                            {cat.valor_total !== undefined && cat.valor_total > 0 && (
                              <div className="resumen-stat">
                                <span className="resumen-stat-label">Valor:</span>
                                <span className="resumen-stat-value" style={{ color: '#4caf50', fontWeight: '600' }}>
                                  ${cat.valor_total.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </span>
                              </div>
                            )}
                            {cat.peso_total !== undefined && cat.peso_total > 0 && (
                              <div className="resumen-stat">
                                <span className="resumen-stat-label">Peso:</span>
                                <span className="resumen-stat-value">
                                  {cat.peso_total.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
                                </span>
                              </div>
                            )}
                            <div className="resumen-stat">
                              <span className="resumen-stat-label">Movimientos:</span>
                              <span className="resumen-stat-value">{cat.movimientos}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <p>No hay datos de inventario disponibles para este hierro.</p>
              )}
            </div>
          </div>
        )}
        
        {/* Graphs section as its own main container */}
        <div className="proceso-calculo-container-graphs dashboard-container">
          <div className="time-range-selector">
            <h3 className="rubik600x35">Rango de Tiempo:</h3>
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="rubik600x35"
            >
              <option value="Mismo mes año anterior">Mismo mes año anterior</option>
              <option value="3 Meses">3 Meses</option>
              <option value="6 Meses">6 Meses</option>
              <option value="12 Meses">12 Meses</option>
              <option value="Por año">Por año</option>
            </select>
            {selectedTimeRange === 'Por año' && (
              <>
                <h3 className="rubik600x35">Año:</h3>
                <select
                  value={selectedYearForGraphs}
                  onChange={(e) => setSelectedYearForGraphs(e.target.value)}
                  className="rubik600x35"
                >
                  {getUniqueYears().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div className="graph-section">
            <h3 className="rubik600x35">Histórico Mensual</h3>
            <div className="graph-wrapper">
              {barChartData && (
                <Bar data={barChartData} options={chartOptions} height={300} />
              )}
            </div>
          </div>
          <div className="graph-section rubro-summary-section">
            <div className="rubro-summary-header">
              <h3 className="rubik600x35">Resumen anual por rubro</h3>
              <div className="rubro-summary-year-select">
                <span className="rubik600x35">Año:</span>
                <select
                  value={selectedRubroSummaryYear}
                  onChange={(e) => setSelectedRubroSummaryYear(e.target.value)}
                  className="rubik600x35"
                >
                  {getUniqueYears().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            {rubroYearSummary.some(item => item.mesesConDatos > 0) ? (
              <div className="rubro-summary-layout">
                <div className="graph-wrapper rubro-summary-donut">
                  {rubroSummaryDonutData ? (
                    <div className="rubro-summary-donut-inner">
                      <div className="rubro-summary-donut-chart">
                        <Pie data={rubroSummaryDonutData} options={rubroSummaryDonutOptions} height={240} />
                      </div>
                      <ul className="rubro-summary-legend" aria-label="Detalle por rubro">
                        {rubroDonutItems.map(item => (
                          <li key={item.rubro} className="rubro-summary-legend-item">
                            <span
                              className="rubro-summary-color-dot"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="rubro-summary-legend-label">{item.rubro}</span>
                            <span className="rubro-summary-legend-pct" style={{ color: item.color }}>
                              {item.porcentaje.toFixed(1)}%
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="rubik600x35 rubro-summary-empty">No hay montos acumulados para mostrar en el gráfico.</p>
                  )}
                </div>
                <div className="rubro-summary-cards">
                  {rubroYearSummary.map(item => (
                    <div
                      key={item.rubro}
                      className="rubro-summary-card"
                      style={{ borderLeftColor: groupColors[item.rubro] || '#828282' }}
                    >
                      <div className="rubro-summary-card-header">
                        <span
                          className="rubro-summary-color-dot"
                          style={{ backgroundColor: groupColors[item.rubro] || '#828282' }}
                        />
                        <span className="rubro-summary-card-title rubik600x35">{item.rubro}</span>
                      </div>
                      <div className="rubro-summary-card-metrics">
                        <div className="rubro-summary-metric">
                          <span className="rubro-summary-metric-label">Acumulado del año</span>
                          <span className="rubro-summary-metric-value">${formatNumber(item.ytd)}</span>
                        </div>
                        <div className="rubro-summary-metric">
                          <span className="rubro-summary-metric-label">Prom. mensual</span>
                          <span className="rubro-summary-metric-value">${formatNumber(item.promedioMensual)}</span>
                        </div>
                        <div className="rubro-summary-metric">
                          <span className="rubro-summary-metric-label">Proy. cierre</span>
                          <span className="rubro-summary-metric-value">${formatNumber(item.proyeccionCierre)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="rubik600x35 rubro-summary-footnote">
                  Proyección de cierre = promedio mensual × 12
                  {rubroYearSummary[0]?.mesesConDatos
                    ? ` (${rubroYearSummary[0].mesesConDatos} meses con datos).`
                    : '.'}
                </p>
              </div>
            ) : (
              <p className="rubik600x35 rubro-summary-empty">
                No hay datos para el año {selectedRubroSummaryYear || 'seleccionado'}.
              </p>
            )}
          </div>
          <div className="graph-section">
            <h3 className="rubik600x35">Histórico de Ingresos, Costos y Gastos</h3>
            <div className="graph-wrapper">
              {lineChartData && (
                <Line data={lineChartData} options={chartOptions} height={300} />
              )}
            </div>
          </div>
          <div className="graph-section">
            <h3 className="rubik600x35">Indicador ICE - Índice de Cobertura de Egresos</h3>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              margin: '0 0 15px 0' 
            }}>
              <p className="rubik600x35" style={{ 
                fontSize: '14px', 
                color: 'var(--dl-color-theme-neutral-dark)', 
                margin: '0'
              }}>
                Valores ≥ 1.0 indican cobertura positiva.
              </p>
              <p className="rubik600x35" style={{ 
                fontSize: '14px', 
                color: 'var(--dl-color-ganando-botones)', 
                fontWeight: 'bold',
                margin: '0'
              }}>
                {(() => {
                  if (!historicalData?.historico) return 'Promedio Periodo: N/A';
                  
                  const filteredData = filterDataByTimeRange(historicalData.historico);
                  const iceValues = filteredData.map(item => {
                    const ingresos = item.total_ingresos || 0;
                    const costos = item.total_costos || 0;
                    const gastos = item.total_gastos || 0;
                    const totalEgresos = costos + gastos;
                    
                    if (totalEgresos === 0) {
                      return ingresos > 0 ? 100 : 0;
                    }
                    
                    return (ingresos / totalEgresos);
                  });
                  
                  const validValues = iceValues.filter(value => !isNaN(value) && isFinite(value));
                  
                  if (validValues.length === 0) return 'Promedio Periodo: N/A';
                  
                  const average = validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
                  
                  return `Promedio Periodo: ${average.toFixed(2)}`;
                })()}
              </p>
            </div>
            <div className="graph-wrapper">
              {iceChartData && (
                <Line data={iceChartData} options={iceChartOptions} height={300} />
              )}
            </div>
          </div>
          </div>
        </div>
        </div>
        
        {/* Popup de imagen */}
      {isImagePopupOpen && fincaInfo?.imagen && (
        <div 
          className="image-popup-overlay" 
          onClick={() => setIsImagePopupOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
        >
          <div 
            className="image-popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '80vw',
              height: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'default'
            }}
          >
            {/* Botón cerrar */}
            <button
              onClick={() => setIsImagePopupOpen(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '20px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
              }}
            >
              ×
            </button>
            
            {/* Imagen */}
            <img
              src={`${API_URL}/api/images/${fincaInfo?.imagen}`}
              alt={fincaName || ''}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '10px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
              }}
            />
          </div>
        </div>
      )}
      
      {/* Loading overlay para el modal de hierro */}
      <LoadingOverlay isLoading={loadingModalHierro} />
    </div>
  )
}

export default ProcesoCalculo